import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

function SecurityIP({ editMode = false }) {
  // Mode observation : ce que la règle AURAIT refusé, sans avoir rien bloqué.
  const [observations, setObservations] = useState([]);
  const [obsJours, setObsJours] = useState(14);
  const [obsChargement, setObsChargement] = useState(false);

  const [restrictions, setRestrictions] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('concession'); // 'concession' | 'role'
  const [addConcessionId, setAddConcessionId] = useState('');
  const [addRole, setAddRole] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingIps, setEditingIps] = useState('');
  const [newIps, setNewIps] = useState('');
  const [error, setError] = useState('');

  const ROLES_SANS_CONCESSION = [
    { value: 'super-admin', label: 'Super-admin' },
    { value: 'teleoperateur', label: 'Téléopérateur' },
  ];

  const load = useCallback(async () => {
    try {
      const [resRestrictions, resConcessions] = await Promise.all([
        api.get('/ip-security'),
        api.get('/concessions'),
      ]);
      setRestrictions(resRestrictions.data);
      setConcessions(resConcessions.data.concessions || []);
    } catch {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ⚠️ Déclaré ICI, avec les autres hooks : plus bas dans le composant se trouve
  // un retour anticipé (`if (loading)`), et un hook placé après ne serait pas
  // appelé à chaque rendu — React l'interdit.
  const chargerObservations = useCallback(async (jours) => {
    setObsChargement(true);
    try {
      const { data } = await api.get('/ip-security/observations', { params: { jours } });
      setObservations(data.lignes || []);
    } catch {
      setObservations([]);
    } finally {
      setObsChargement(false);
    }
  }, []);

  useEffect(() => { chargerObservations(obsJours); }, [chargerObservations, obsJours]);

  const handleAdd = async () => {
    setSaving(true);
    setError('');
    try {
      const ips = newIps.split(/[\n,;]+/).map(ip => ip.trim()).filter(Boolean);
      const body = {
        concession_id: addType === 'concession' ? parseInt(addConcessionId) : null,
        role: addType === 'role' ? addRole : null,
        allowed_ips: ips,
      };
      await api.post('/ip-security', body);
      setShowAddModal(false);
      setNewIps('');
      setAddConcessionId('');
      setAddRole('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    setError('');
    try {
      const ips = editingIps.split(/[\n,;]+/).map(ip => ip.trim()).filter(Boolean);
      await api.put(`/ip-security/${id}`, { allowed_ips: ips });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette restriction IP ?')) return;
    try {
      await api.delete(`/ip-security/${id}`);
      await load();
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditingIps((r.allowed_ips || []).join('\n'));
  };

  // Concessions et rôles déjà utilisés
  const usedConcessionIds = restrictions.filter(r => r.concession_id).map(r => r.concession_id);
  const usedRoles = restrictions.filter(r => r.role).map(r => r.role);
  const availableConcessions = concessions.filter(c => !usedConcessionIds.includes(c.id));
  const availableRoles = ROLES_SANS_CONCESSION.filter(r => !usedRoles.includes(r.value));

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>;
  }

  // Bascule application ↔ observation. En observation la règle n'interdit rien :
  // elle journalise. C'est le palier qui permet de bâtir la liste sur des faits.
  const basculerObservation = async (r) => {
    try {
      await api.put(`/ip-security/${r.id}`, { allowed_ips: r.allowed_ips || [], observation: !r.observation });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Bascule impossible');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '700px' }}>
            Définissez les adresses IP autorisées par concession ou par rôle. Si aucune IP n'est définie, l'accès est libre.
            Si une ou plusieurs IP sont définies, seuls les collaborateurs se connectant depuis ces adresses auront accès au CRM.
          </div>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setShowAddModal(true); setError(''); }}
          style={{ whiteSpace: 'nowrap' }}
        >
          ➕ Ajouter une restriction
        </button>
      </div>

      {error && (
        <div style={{
          background: 'var(--color-error-light)', border: '1px solid light-dark(#fecaca, var(--border-strong))', color: 'var(--color-error-dark)',
          padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {/* Restrictions par concession */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
          🏢 Restrictions par concession
        </h3>
        {restrictions.filter(r => r.concession_id).length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px 0' }}>
            Aucune restriction par concession configurée — accès libre pour toutes les concessions.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Concession</th>
                  <th>Adresses IP autorisées</th>
                  <th style={{ width: '170px' }}>Mode</th>
                  <th style={{ width: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {restrictions.filter(r => r.concession_id).map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: '600', fontSize: '13px' }}>{r.concession_nom}</td>
                    <td>
                      {editingId === r.id ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          <textarea
                            value={editingIps}
                            onChange={e => setEditingIps(e.target.value)}
                            rows={3}
                            style={{
                              flex: 1, fontSize: '12px', fontFamily: 'monospace',
                              padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-strong)',
                              resize: 'vertical'
                            }}
                            placeholder="Une IP par ligne"
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <button className="btn-primary" style={{ fontSize: '11px', padding: '5px 10px' }}
                              onClick={() => handleUpdate(r.id)} disabled={saving}>
                              ✓
                            </button>
                            <button className="btn-secondary" style={{ fontSize: '11px', padding: '5px 10px' }}
                              onClick={() => setEditingId(null)}>
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(r.allowed_ips || []).length === 0 ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>Aucune IP — accès libre</span>
                          ) : (
                            r.allowed_ips.map((ip, i) => (
                              <span key={i} style={{
                                background: 'var(--bg-muted)', borderRadius: '4px', padding: '2px 8px',
                                fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)'
                              }}>
                                {ip}
                              </span>
                            ))
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {/* En observation, la règle n'interdit rien : elle note ce
                          qu'elle aurait refusé. C'est le palier qui évite de
                          découvrir une liste blanche en bloquant l'atelier. */}
                      <button
                        onClick={() => basculerObservation(r)}
                        title={r.observation
                          ? 'Actuellement en observation : rien n’est bloqué, tout est journalisé. Cliquer pour APPLIQUER la règle.'
                          : 'Actuellement appliquée : les adresses hors liste sont refusées. Cliquer pour passer en observation.'}
                        style={{
                          fontSize: '11px', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer',
                          border: `1px solid ${r.observation ? 'light-dark(#fed7aa, var(--border-strong))' : 'light-dark(#bbf7d0, var(--border-strong))'}`,
                          background: r.observation ? 'light-dark(#fff7ed, var(--color-paused-light))' : 'light-dark(#f0fdf4, var(--bg-muted))',
                          color: r.observation ? 'light-dark(#c2410c, var(--color-warning-dark))' : 'light-dark(#15803d, var(--color-success-dark))', fontWeight: 600,
                        }}
                      >
                        {r.observation ? '👁️ Observation' : '🔒 Appliquée'}
                      </button>
                    </td>
                    <td>
                      {editingId !== r.id && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn-secondary" style={{ fontSize: '11px', padding: '5px 10px' }}
                            onClick={() => startEdit(r)}>
                            ✏️ Modifier
                          </button>
                          {editMode && (
                            <button style={{
                              fontSize: '11px', padding: '5px 10px', background: 'var(--color-error-light)',
                              color: 'var(--color-error-dark)', border: '1px solid light-dark(#fecaca, var(--border-strong))', borderRadius: '6px', cursor: 'pointer'
                            }}
                              onClick={() => handleDelete(r.id)}>
                              🗑️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restrictions par rôle */}
      <div>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
          👤 Restrictions par rôle (sans concession)
        </h3>
        {restrictions.filter(r => r.role).length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px 0' }}>
            Aucune restriction par rôle configurée — accès libre pour super-admins et téléopérateurs.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Rôle</th>
                  <th>Adresses IP autorisées</th>
                  <th style={{ width: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {restrictions.filter(r => r.role).map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: '600', fontSize: '13px' }}>
                      {r.role === 'super-admin' ? 'Super-admin' : r.role === 'teleoperateur' ? 'Téléopérateur' : r.role}
                    </td>
                    <td>
                      {editingId === r.id ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          <textarea
                            value={editingIps}
                            onChange={e => setEditingIps(e.target.value)}
                            rows={3}
                            style={{
                              flex: 1, fontSize: '12px', fontFamily: 'monospace',
                              padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-strong)',
                              resize: 'vertical'
                            }}
                            placeholder="Une IP par ligne"
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <button className="btn-primary" style={{ fontSize: '11px', padding: '5px 10px' }}
                              onClick={() => handleUpdate(r.id)} disabled={saving}>
                              ✓
                            </button>
                            <button className="btn-secondary" style={{ fontSize: '11px', padding: '5px 10px' }}
                              onClick={() => setEditingId(null)}>
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(r.allowed_ips || []).length === 0 ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>Aucune IP — accès libre</span>
                          ) : (
                            r.allowed_ips.map((ip, i) => (
                              <span key={i} style={{
                                background: 'var(--bg-muted)', borderRadius: '4px', padding: '2px 8px',
                                fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)'
                              }}>
                                {ip}
                              </span>
                            ))
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {editingId !== r.id && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn-secondary" style={{ fontSize: '11px', padding: '5px 10px' }}
                            onClick={() => startEdit(r)}>
                            ✏️ Modifier
                          </button>
                          {editMode && (
                            <button style={{
                              fontSize: '11px', padding: '5px 10px', background: 'var(--color-error-light)',
                              color: 'var(--color-error-dark)', border: '1px solid light-dark(#fecaca, var(--border-strong))', borderRadius: '6px', cursor: 'pointer'
                            }}
                              onClick={() => handleDelete(r.id)}>
                              🗑️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modale d'ajout */}
      {/* ── Ce que les règles en observation AURAIENT refusé ─────────────────
          La matière première pour bâtir la liste blanche : qui se connecte
          d'où, à quel point. Les lignes marquées « borne » rappellent que la
          MÊME règle garde les bornes publiques de la concession. */}
      <div style={{ marginTop: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            👁️ Observations — ce qui serait refusé
          </h3>
          <select value={obsJours} onChange={e => setObsJours(Number(e.target.value))}
            style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-strong)' }}>
            <option value={1}>Aujourd'hui</option>
            <option value={7}>7 jours</option>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
          </select>
          <button className="btn-secondary" style={{ fontSize: '11px', padding: '5px 10px' }}
            onClick={() => chargerObservations(obsJours)}>↻ Actualiser</button>
        </div>
        {obsChargement ? (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Chargement…</div>
        ) : observations.length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Rien à signaler : aucune règle en observation n'a rencontré d'adresse hors liste sur la période.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Adresse IP</th><th>Concession</th><th style={{ width: '110px' }}>Requêtes</th>
                  <th style={{ width: '110px' }}>Utilisateurs</th><th>Exemples</th><th style={{ width: '160px' }}>Période</th>
                </tr>
              </thead>
              <tbody>
                {observations.map(o => (
                  <tr key={`${o.ip}-${o.concession_id}`}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {o.ip}
                      {o.inclut_borne && <span title="Une borne publique de cette concession serait coupée aussi" style={{ marginLeft: 6 }}>🖥️</span>}
                    </td>
                    <td>{o.concession_nom || '—'}</td>
                    <td>{o.requetes}</td>
                    <td>{o.utilisateurs}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{(o.exemples_utilisateurs || []).join(', ')}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{o.depuis} → {o.jusqu_a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➕ Nouvelle restriction IP</h2>
              <button onClick={() => setShowAddModal(false)} className="modal-close-btn">×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
                  Type de restriction
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className={addType === 'concession' ? 'btn-primary' : 'btn-secondary'}
                    style={{ fontSize: '12px', padding: '6px 14px' }}
                    onClick={() => setAddType('concession')}
                  >
                    🏢 Par concession
                  </button>
                  <button
                    className={addType === 'role' ? 'btn-primary' : 'btn-secondary'}
                    style={{ fontSize: '12px', padding: '6px 14px' }}
                    onClick={() => setAddType('role')}
                  >
                    👤 Par rôle
                  </button>
                </div>
              </div>

              {addType === 'concession' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
                    Concession
                  </label>
                  {availableConcessions.length === 0 ? (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Toutes les concessions ont déjà une restriction configurée.
                    </div>
                  ) : (
                    <select
                      value={addConcessionId}
                      onChange={e => setAddConcessionId(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: '6px',
                        border: '1px solid var(--border-strong)', fontSize: '13px'
                      }}
                    >
                      <option value="">— Sélectionner —</option>
                      {availableConcessions.map(c => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {addType === 'role' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
                    Rôle
                  </label>
                  {availableRoles.length === 0 ? (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Tous les rôles ont déjà une restriction configurée.
                    </div>
                  ) : (
                    <select
                      value={addRole}
                      onChange={e => setAddRole(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: '6px',
                        border: '1px solid var(--border-strong)', fontSize: '13px'
                      }}
                    >
                      <option value="">— Sélectionner —</option>
                      {availableRoles.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
                  Adresses IP autorisées
                </label>
                <textarea
                  value={newIps}
                  onChange={e => setNewIps(e.target.value)}
                  rows={4}
                  placeholder="Une adresse IP par ligne (ex: 82.120.45.12)"
                  style={{
                    width: '100%', fontSize: '12px', fontFamily: 'monospace',
                    padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border-strong)',
                    resize: 'vertical', boxSizing: 'border-box'
                  }}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Laissez vide pour ne pas restreindre. Vous pourrez ajouter les IP plus tard.
                </div>
              </div>

              {error && (
                <div style={{
                  background: 'var(--color-error-light)', border: '1px solid light-dark(#fecaca, var(--border-strong))', color: 'var(--color-error-dark)',
                  padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '12px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Annuler
                </button>
                <button
                  className="btn-primary"
                  onClick={handleAdd}
                  disabled={saving || (addType === 'concession' ? !addConcessionId : !addRole)}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SecurityIP;
